import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactionQueryRole, ReactionSearchHit } from '@peruselab/api-client/reactionSearch';
import { Icon } from '../Icon';
import { ResultMatchDetails } from './ResultMatchDetails';
import { ReactionArrowGlyph } from '../ReactionArrowGlyph';
import type { ReactionArrowAnnotationKind } from '@peruselab/structure-editor/chemistry/reactionParticipant';
import { ReactionPicture, useReactionPicture } from './ReactionPicture';
import { usePaperAdditions } from './paperAdditions';
import { AddToPaperButton, useAddToPaper } from './AddToPaperButton';
import { useDragSource } from '../../dnd/DragDrop';
import type { StructureDragPayload } from '../../dnd/payloads';
import { StructureZoomDialog, ZoomablePicture, type ZoomPicture } from '../StructureZoomDialog';

interface ReactionResultCardProps {
  readonly reactionId: string;
  readonly title: string | null;
  /** The saved arrow's kind, when known (drawn in the footer). */
  readonly arrowKind?: string | null | undefined;
  /** The overlay's score line. */
  readonly scoreLabel: ReactNode;
  /** Why it matched, when there is something to say. */
  readonly why?: string | undefined;
  readonly onOpen?: ((id: string) => void) | undefined;
  readonly openDisabled?: boolean | undefined;
}

/**
 * #704 -- one reaction found by a search: its picture, its score, why it matched, and a way to
 * open it. #743 -- any search shows reactions this way (the reaction search with an arrow on the
 * paper, and a plain structure or text search). #1141 -- the picture is the reaction as drawn.
 */
export function ReactionResultCard({
  reactionId,
  title,
  arrowKind,
  scoreLabel,
  why,
  onOpen,
  openDisabled
}: ReactionResultCardProps) {
  const { t } = useTranslation();
  const name = title?.trim() || t('reactionResults.untitled');
  // #1141 (ADR 0025) -- drawn as its paper draws it, the author's layout scaled into the card.
  // #984 -- in a card as wide as any other: a larger reaction opens larger with a click.
  const drawn = useReactionPicture(reactionId);
  const [enlarged, setEnlarged] = useState(false);
  const picture = useMemo<ZoomPicture | null>(
    () =>
      drawn.status === 'ready'
        ? { kind: 'drawn', width: drawn.width, height: drawn.height, content: <ReactionPicture state={drawn} /> }
        : null,
    [drawn]
  );
  // #744 -- "+" (and dragging the card onto the paper) puts the whole reaction on the paper, one
  // undo step, in place of the paper's own reaction if it has one.
  const additions = usePaperAdditions();
  const addReaction = useMemo(
    () =>
      additions
        ? async () => {
            await additions.addReaction(reactionId);
            return null;
          }
        : undefined,
    [additions, reactionId]
  );
  const adding = useAddToPaper(addReaction);
  const { add } = adding;
  const payload = useMemo<StructureDragPayload | null>(
    () => (additions ? { kind: 'structure', label: name, place: () => void add() } : null),
    [add, additions, name]
  );
  const drag = useDragSource(payload, <span className="dnd-ghost-chip">{name}</span>);
  return (
    <div className="search-result-card reaction-result-card" data-testid="reaction-result-card" {...drag}>
      <div className="search-result-thumb reaction-result-scheme">
        {picture ? (
          <ZoomablePicture name={name} onOpen={() => setEnlarged(true)}>
            <ReactionPicture state={drawn} />
          </ZoomablePicture>
        ) : (
          <ReactionPicture state={drawn} />
        )}
      </div>
      {enlarged && picture ? (
        <StructureZoomDialog picture={picture} name={name} matchLabel={scoreLabel} onClose={() => setEnlarged(false)} />
      ) : null}
      <div className="search-result-card-overlay-top">
        {scoreLabel}
        {why ? <ResultMatchDetails text={why} testId="reaction-result-why" /> : null}
      </div>
      <div className="search-result-card-footer">
        <span className="search-results-id" data-testid="result-name">
          {name}
        </span>
        {arrowKind ? <ReactionArrowGlyph kind={arrowKind as ReactionArrowAnnotationKind} /> : null}
        {additions ? <AddToPaperButton moleculeId="" adding={adding} boxes={null} /> : null}
        {onOpen ? (
          <div className="result-container-button">
            <button
              type="button"
              className="result-container-trigger"
              data-testid="reaction-result-open"
              aria-label={t('reactionResults.open', { title: name })}
              title={t('reactionResults.open', { title: name })}
              disabled={openDisabled}
              onClick={() => onOpen(reactionId)}
            >
              <Icon name="openInNew" size="1.1rem" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** #704 -- a hit of the reaction search (an arrow on the paper): its role-aware score and matches. */
export function ReactionHitCard({
  hit,
  formulaOf,
  onOpen,
  openDisabled
}: {
  readonly hit: ReactionSearchHit;
  readonly formulaOf: (role: ReactionQueryRole, index: number) => string;
  readonly onOpen?: ((id: string) => void) | undefined;
  readonly openDisabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const why = hit.matches
    .map((match) =>
      t('reactionResults.matched', {
        formula: formulaOf(match.queryRole, match.queryIndex),
        role: t(`reactionResults.roles.${match.participantRole}`)
      })
    )
    .join(' · ');
  return (
    <ReactionResultCard
      reactionId={hit.reactionId}
      title={hit.title}
      arrowKind={hit.arrowKind}
      scoreLabel={
        <span className="search-result-card-label">
          {hit.matches.length > 0
            ? t('reactionResults.score', { score: hit.score.toFixed(2) })
            : t('reactionResults.arrowMatch')}
        </span>
      }
      why={why || undefined}
      onOpen={onOpen}
      openDisabled={openDisabled}
    />
  );
}
